import { useState, useEffect, useCallback } from "react";
import {
  adminApi,
  type AIGenerationJob,
  type AIGenerationParams,
} from "@/features/admin/api/adminApi";
import toast from "react-hot-toast";

interface UseAIGenerationOptions {
  onComplete?: (job: AIGenerationJob) => void;
  onError?: (error: string) => void;
  pollingInterval?: number;
}

interface UseAIGenerationReturn {
  // State
  isGenerating: boolean;
  job: AIGenerationJob | null;
  error: string | null;

  // Actions
  startGeneration: (params: AIGenerationParams) => Promise<void>;
  confirmCourse: () => Promise<{ id: string; slug: string } | null>;
  cancelGeneration: () => Promise<void>;
  reset: () => void;
}

export function useAIGeneration(
  options: UseAIGenerationOptions = {},
): UseAIGenerationReturn {
  const { onComplete, onError, pollingInterval = 2000 } = options;

  const [isGenerating, setIsGenerating] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<AIGenerationJob | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Poll for job status
  useEffect(() => {
    if (!jobId || !isGenerating) return;

    const pollStatus = async () => {
      try {
        const updatedJob = await adminApi.getGenerationJob(jobId);
        setJob(updatedJob);

        if (updatedJob.status === "COMPLETED") {
          setIsGenerating(false);
          onComplete?.(updatedJob);
          toast.success("¡Curso generado exitosamente!");
        } else if (updatedJob.status === "FAILED") {
          setIsGenerating(false);
          const errorMsg =
            updatedJob.errorMessage || "Error desconocido en la generación";
          setError(errorMsg);
          onError?.(errorMsg);
          toast.error("Error en la generación del curso");
        }
      } catch (err) {
        console.error("Error polling job status:", err);
      }
    };

    // Initial poll
    pollStatus();

    // Set up interval
    const interval = setInterval(pollStatus, pollingInterval);

    return () => clearInterval(interval);
  }, [jobId, isGenerating, pollingInterval, onComplete, onError]);

  const startGeneration = useCallback(
    async (params: AIGenerationParams) => {
      setError(null);
      setJob(null);
      setIsGenerating(true);

      try {
        const result = await adminApi.generateCourse(params);
        setJobId(result.jobId);
        toast.success("Generación iniciada...");
      } catch (err) {
        setIsGenerating(false);
        const errorMsg =
          err instanceof Error ? err.message : "Error al iniciar la generación";
        setError(errorMsg);
        onError?.(errorMsg);
        toast.error(errorMsg);
      }
    },
    [onError],
  );

  const confirmCourse = useCallback(async () => {
    if (!jobId || job?.status !== "COMPLETED") {
      toast.error("No hay un curso listo para confirmar");
      return null;
    }

    try {
      const course = await adminApi.confirmGeneration(jobId);
      toast.success(`Curso "${course.title}" creado exitosamente`);
      return { id: course.id, slug: course.slug };
    } catch (err) {
      const errorMsg =
        err instanceof Error ? err.message : "Error al crear el curso";
      toast.error(errorMsg);
      return null;
    }
  }, [jobId, job]);

  const cancelGeneration = useCallback(async () => {
    if (jobId) {
      try {
        await adminApi.deleteGenerationJob(jobId);
        toast.success("Generación cancelada");
      } catch (err) {
        console.error("Error canceling generation:", err);
      }
    }
    setIsGenerating(false);
    setJobId(null);
    setJob(null);
    setError(null);
  }, [jobId]);

  const reset = useCallback(() => {
    setIsGenerating(false);
    setJobId(null);
    setJob(null);
    setError(null);
  }, []);

  return {
    isGenerating,
    job,
    error,
    startGeneration,
    confirmCourse,
    cancelGeneration,
    reset,
  };
}
